import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { StageHistoryEntry } from "@/hooks/use-pipeline";
import { resolveStageDisplay } from "@/lib/pipeline-display";
import type { PipelineRow } from "@/types/pipeline";
import { MockProperty } from "@/data/mockProperties";
import { Clock, ChevronRight } from "lucide-react";

interface SavedPropertyRow {
  id: string;
  pipeline_id?: string | null;
  property_data: unknown;
  created_at: string;
}

interface PipelineTimelineProps {
  pipelines: PipelineRow[];
  savedProperties: SavedPropertyRow[];
  getHistoryForProperty: (savedPropertyId: string) => StageHistoryEntry[];
}

function formatDuration(ms: number): string {
  const hours = Math.floor(ms / 3600000);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h`;
  const mins = Math.floor(ms / 60000);
  return `${mins}m`;
}

interface StageDuration {
  /** pipeline_id, or null when the row has no resolved pipeline (rendered as "Unknown"). */
  stage: string | null;
  duration: number;
  enteredAt: string;
}

function computeStageDurations(
  history: StageHistoryEntry[],
  currentStage: string | null,
  createdAt: string,
): StageDuration[] {
  if (history.length === 0) {
    return [
      {
        stage: currentStage,
        duration: Date.now() - new Date(createdAt).getTime(),
        enteredAt: createdAt,
      },
    ];
  }

  const durations: StageDuration[] = [];

  const firstChange = new Date(history[0].changed_at).getTime();
  const created = new Date(createdAt).getTime();
  durations.push({
    stage: history[0].from_pipeline_id ?? currentStage,
    duration: firstChange - created,
    enteredAt: createdAt,
  });

  for (let i = 0; i < history.length; i++) {
    const enteredAt = new Date(history[i].changed_at).getTime();
    const exitedAt =
      i < history.length - 1
        ? new Date(history[i + 1].changed_at).getTime()
        : Date.now();
    durations.push({
      stage: history[i].to_pipeline_id,
      duration: exitedAt - enteredAt,
      enteredAt: history[i].changed_at,
    });
  }

  return durations;
}

export function PipelineTimeline({
  pipelines,
  savedProperties,
  getHistoryForProperty,
}: PipelineTimelineProps) {
  const propertyTimelines = useMemo(() => {
    return savedProperties
      .map((sp) => {
        const history = getHistoryForProperty(sp.id);
        const currentStage = sp.pipeline_id ?? pipelines[0]?.id ?? null;
        const durations = computeStageDurations(
          history,
          currentStage,
          sp.created_at,
        );
        const property = sp.property_data as unknown as MockProperty;
        return { id: sp.id, property, currentStage, durations };
      })
      .filter((t) => t.durations.length > 0);
  }, [savedProperties, getHistoryForProperty, pipelines]);

  if (propertyTimelines.length === 0) {
    return (
      <Card className="border-border/50">
        <CardContent className="p-6 text-center">
          <Clock className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            Move properties between stages to see timeline data
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" />
          Stage Timeline
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {propertyTimelines.map(({ id, property, currentStage, durations }) => {
          const totalDuration = durations.reduce((sum, d) => sum + d.duration, 0);
          const current = resolveStageDisplay(currentStage, pipelines);

          return (
            <div key={id} className="space-y-2">
              {/* Property header */}
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {property?.address || "Unknown"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {property?.city}, {property?.state} · Total:{" "}
                    {formatDuration(totalDuration)}
                  </p>
                </div>
                <Badge variant="outline" className="text-xs shrink-0 ml-2">
                  <span
                    className="w-1.5 h-1.5 rounded-full mr-1.5"
                    style={{ backgroundColor: current.color }}
                  />
                  {current.label}
                </Badge>
              </div>

              {/* Timeline bar */}
              <div className="flex rounded-md overflow-hidden h-6 bg-muted/30 border border-border/50">
                {durations.map((d, i) => {
                  const pct =
                    totalDuration > 0 ? (d.duration / totalDuration) * 100 : 0;
                  if (pct < 1) return null;
                  const display = resolveStageDisplay(d.stage, pipelines);
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-center text-[10px] text-white font-medium transition-all relative group"
                      style={{
                        width: `${pct}%`,
                        minWidth: pct > 5 ? undefined : "4px",
                        backgroundColor: display.color,
                      }}
                      title={`${display.label}: ${formatDuration(d.duration)}`}
                    >
                      {pct > 12 && (
                        <span className="truncate px-1">{display.label}</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Stage chips */}
              <div className="flex flex-wrap gap-1">
                {durations.map((d, i) => {
                  const display = resolveStageDisplay(d.stage, pipelines);
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-1 text-[10px] text-muted-foreground"
                    >
                      {i > 0 && (
                        <ChevronRight className="h-2.5 w-2.5 text-muted-foreground/40" />
                      )}
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: display.color }}
                      />
                      <span>{display.label}</span>
                      <span className="font-medium text-foreground">
                        {formatDuration(d.duration)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
